'use client';

import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import MarketIcon from 'src/components/Utils/MarketIcon';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useWalletBalance } from 'src/hooks/query/walletBalances/useWalletBalance';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import useModal from 'src/components/Modals/hooks/useModal';
import Button from 'src/components/primitives/Button/Button';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { PRESETS } from 'src/utils/formatNumber/presets';

const AssetBalanceRow = ({
	spotMarketConfig,
	hideZeroBalance,
	onClick,
}: {
	spotMarketConfig: SpotMarketConfig;
	hideZeroBalance: boolean;
	onClick: () => void;
}) => {
	const { symbol, marketIndex } = spotMarketConfig;

	const assetBalance = useWalletBalance(
		spotMarketConfig.symbol,
		spotMarketConfig.precisionExp
	);

	const balance = assetBalance.base;
	const velocityClient = useVelocityClient();

	const oraclePrice = BigNum.from(
		velocityClient?.getOracleDataForSpotMarket(marketIndex)?.price,
		PRICE_PRECISION_EXP
	);
	const balanceInUsd = balance
		.shift(PRICE_PRECISION_EXP)
		.mul(oraclePrice)
		.shiftTo(QUOTE_PRECISION_EXP);

	if (hideZeroBalance && balance.eqZero()) {
		return null;
	}

	return (
		<div
			className="flex cursor-pointer items-center justify-between bg-container-bg p-lg hover:bg-button-disabled"
			onClick={onClick}
		>
			<div className="flex items-center gap-sm">
				<MarketIcon marketSymbol={symbol} sizeClass="size-xl" />
				<span className="typo-label-sm text-text-default">{symbol}</span>
			</div>
			<div className="flex flex-col items-end gap-1">
				<span className="typo-label-sm text-text-default">
					{balance.toTradePrecision()}
				</span>
				<span className="typo-body-xs text-text-secondary">
					<NumberDisplay value={balanceInUsd} options={PRESETS.usd} />
				</span>
			</div>
		</div>
	);
};

const WalletBalancesTab = ({ onClose }: { onClose: () => void }) => {
	const { show } = useModal();
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();

	const handleOpenDepositModal = (collateralIndex = 0) => {
		show({
			type: 'deposit',
			props: { collateralIndex },
		});
		onClose();
	};

	const handleOpenSendModal = () => {
		show({ type: 'send_from_wallet', props: {} });
		onClose();
	};

	const handleOpenReceiveModal = () => {
		show({ type: 'receive_to_wallet', props: {} });
		onClose();
	};

	return (
		<div className="flex flex-col gap-md">
			<div className="flex flex-col overflow-hidden rounded-sm border border-stroke-divider">
				<div className="flex justify-between border-b border-stroke-divider px-md py-sm">
					<span className="typo-body-xs text-text-secondary">Market</span>
					<span className="text-right typo-body-xs text-text-secondary">
						Balances
					</span>
				</div>
				<div className="flex flex-col">
					{spotMarketsSortedByMarketIndex.map((spotMarketConfig) => {
						return (
							<AssetBalanceRow
								spotMarketConfig={spotMarketConfig}
								key={spotMarketConfig.marketIndex}
								hideZeroBalance={false}
								onClick={() => {
									handleOpenDepositModal(spotMarketConfig.marketIndex);
								}}
							/>
						);
					})}
				</div>
			</div>

			{/* Actions */}
			<div className="flex gap-sm">
				<Button
					variant="secondary"
					size="md"
					className="flex-1"
					onClick={() => handleOpenDepositModal()}
				>
					Deposit
				</Button>
				<Button
					variant="secondary"
					size="md"
					className="flex-1"
					onClick={handleOpenReceiveModal}
				>
					Receive
				</Button>
				<Button
					variant="secondary"
					size="md"
					className="flex-1"
					onClick={handleOpenSendModal}
				>
					Send
				</Button>
			</div>
		</div>
	);
};

export default WalletBalancesTab;
